import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPen, faBoxArchive, faRotate, faWallet } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import { useConti } from '../../components/conti/ContiLayout';
import {
  ICONE_RAPPORTI, ICONE, INTESTATARI, TIPI_RAPPORTO, IconaTonda, euro,
} from '../../components/conti/contiTheme';
import { creaRapporto, getRapporti, messaggioErrore, modificaRapporto } from '../../services/contiService';

const COLORI = ['#3b5bdb', '#7048e8', '#1c7ed6', '#0ca678', '#2f9e44', '#f08c00', '#e8590c', '#e03131', '#c2255c', '#495057'];
const VUOTO = {
  denominazione: '', tipo: 'conto_corrente', intestatario: '', iban: '', colore: '', icona: '',
  saldo_iniziale: '', data_saldo_iniziale: '', firefly_asset_account_name: '',
};

const RapportiBancari = () => {
  const { scope, tema } = useConti();
  const [dati, setDati] = useState(null);
  const [caricamento, setCaricamento] = useState(true);
  const [modifica, setModifica] = useState(null); // null = chiuso, oggetto = form
  const [salvataggio, setSalvataggio] = useState(false);
  const [mostraArchiviati, setMostraArchiviati] = useState(false);

  const carica = useCallback(async () => {
    setCaricamento(true);
    try {
      setDati(await getRapporti(scope));
    } catch (e) {
      toast.error(messaggioErrore(e, 'Impossibile caricare i rapporti bancari'));
    } finally {
      setCaricamento(false);
    }
  }, [scope]);

  useEffect(() => { carica(); }, [carica]);

  const apri = (r) => {
    if (r) {
      setModifica({
        id: r.id, denominazione: r.denominazione || '', tipo: r.tipo || 'altro', intestatario: r.intestatario || '',
        iban: r.iban || '', colore: r.colore || '', icona: r.icona || '', firefly_asset_account_name: r.firefly_asset_account_name || '',
        saldo_iniziale: '', data_saldo_iniziale: '',
      });
    } else {
      setModifica({ ...VUOTO, intestatario: scope === 'azienda' ? 'Azienda' : '' });
    }
  };

  const salva = async (e) => {
    e.preventDefault();
    setSalvataggio(true);
    const payload = {
      denominazione: modifica.denominazione, tipo: modifica.tipo, intestatario: modifica.intestatario || null,
      iban: modifica.iban || null, colore: modifica.colore || null, icona: modifica.icona || null,
      firefly_asset_account_name: modifica.firefly_asset_account_name || null,
    };
    try {
      if (modifica.id) {
        await modificaRapporto(modifica.id, payload);
        toast.success('Rapporto aggiornato');
      } else {
        await creaRapporto({
          ...payload, scope,
          saldo_iniziale: modifica.saldo_iniziale === '' ? null : parseFloat(String(modifica.saldo_iniziale).replace(',', '.')),
          data_saldo_iniziale: modifica.data_saldo_iniziale || null,
        });
        toast.success('Rapporto creato e collegato a Firefly');
      }
      setModifica(null);
      carica();
    } catch (err) {
      toast.error(messaggioErrore(err, 'Salvataggio non riuscito'));
    } finally {
      setSalvataggio(false);
    }
  };

  const archivia = async (r) => {
    const msg = r.attivo ? `Disattivare "${r.denominazione}"? Resta in Firefly con i suoi movimenti.` : `Riattivare "${r.denominazione}"?`;
    if (!window.confirm(msg)) return;
    try {
      await modificaRapporto(r.id, { attivo: !r.attivo });
      carica();
    } catch (err) {
      toast.error(messaggioErrore(err));
    }
  };

  const campo = (nome) => (e) => setModifica((m) => ({ ...m, [nome]: e.target.value }));
  const rapporti = (dati?.rapporti || []).filter((r) => mostraArchiviati || r.attivo);

  return (
    <>
      <Row className="g-3 mb-3">
        <Col md={4}>
          <div style={{ background: tema.morbido, borderRadius: 14, padding: '16px 20px' }}>
            <div className="text-muted" style={{ fontSize: 13 }}>Saldo totale dei rapporti attivi</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: tema.scuro }}>{euro(dati?.totale)}</div>
          </div>
        </Col>
        <Col md={8} className="d-flex align-items-center justify-content-md-end gap-2 flex-wrap">
          <Form.Check type="switch" id="mostra-arch" label="Mostra disattivati" checked={mostraArchiviati}
            onChange={(e) => setMostraArchiviati(e.target.checked)} />
          <Button variant="outline-secondary" onClick={carica}><FontAwesomeIcon icon={faRotate} /></Button>
          <Button style={{ background: tema.colore, borderColor: tema.colore }} onClick={() => apri(null)}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />Nuovo rapporto
          </Button>
        </Col>
      </Row>

      {dati && !dati.firefly_ok && (
        <Alert variant="warning">Firefly non risponde: i saldi non sono disponibili in questo momento.</Alert>
      )}
      {caricamento && <div className="text-center py-5"><Spinner animation="border" /></div>}

      {!caricamento && rapporti.length === 0 && (
        <div className="text-center text-muted py-5" style={{ border: '2px dashed #ced4da', borderRadius: 16 }}>
          <FontAwesomeIcon icon={faWallet} size="2x" className="mb-2" />
          <div>Nessun rapporto bancario in {tema.nome}.</div>
          <Button className="mt-3" style={{ background: tema.colore, borderColor: tema.colore }} onClick={() => apri(null)}>
            Crea il primo rapporto
          </Button>
        </div>
      )}

      <Row className="g-3">
        {rapporti.map((r) => (
          <Col key={r.id} sm={6} xl={4}>
            <div style={{
              background: '#fff', borderRadius: 16, padding: 18, borderTop: `5px solid ${r.colore || tema.colore}`,
              boxShadow: '0 2px 10px rgba(0,0,0,.07)', opacity: r.attivo ? 1 : 0.55, height: '100%',
            }}>
              <div className="d-flex align-items-center gap-3">
                <IconaTonda icona={r.icona} colore={r.colore || tema.colore} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 17 }} className="text-truncate">{r.denominazione}</div>
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    {TIPI_RAPPORTO.find((t) => t.v === r.tipo)?.l || 'Rapporto'}{r.intestatario ? ` · ${r.intestatario}` : ''}
                  </div>
                </div>
              </div>
              <div className="mt-3" style={{ fontSize: 26, fontWeight: 700, color: (r.saldo ?? 0) < 0 ? '#e03131' : '#212529' }}>
                {euro(r.saldo)}
              </div>
              {r.riservati != null && (
                <div className="text-muted" style={{ fontSize: 12 }} title="Movimenti riservati: non sono su Firefly e li vedi solo tu">
                  🔒 riservati {euro(r.riservati)} · saldo reale <strong>{euro(r.saldo_reale)}</strong>
                </div>
              )}
              {r.iban && <div className="text-muted" style={{ fontSize: 12 }}>{r.iban}</div>}
              {!r.attivo && <span className="badge bg-secondary mt-1">Disattivato</span>}
              <div className="d-flex gap-2 mt-3">
                <Button size="sm" variant="outline-primary" onClick={() => apri(r)}><FontAwesomeIcon icon={faPen} className="me-1" />Modifica</Button>
                <Button size="sm" variant="outline-secondary" onClick={() => archivia(r)}>
                  <FontAwesomeIcon icon={faBoxArchive} className="me-1" />{r.attivo ? 'Disattiva' : 'Riattiva'}
                </Button>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      <Modal show={!!modifica} onHide={() => setModifica(null)} centered>
        {modifica && (
          <Form onSubmit={salva}>
            <Modal.Header closeButton style={{ background: tema.morbido }}>
              <Modal.Title style={{ color: tema.scuro }}>{modifica.id ? 'Modifica rapporto' : `Nuovo rapporto · ${tema.nome}`}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Nome</Form.Label>
                <Form.Control required value={modifica.denominazione} onChange={campo('denominazione')} placeholder="es. ING Giorgio" />
              </Form.Group>
              <Row className="g-3 mb-3">
                <Col>
                  <Form.Label>Tipo</Form.Label>
                  <Form.Select value={modifica.tipo} onChange={campo('tipo')}>
                    {TIPI_RAPPORTO.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                  </Form.Select>
                </Col>
                <Col>
                  <Form.Label>Intestatario</Form.Label>
                  <Form.Select value={modifica.intestatario} onChange={campo('intestatario')}>
                    <option value="">—</option>
                    {INTESTATARI.map((i) => <option key={i} value={i}>{i}</option>)}
                  </Form.Select>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>IBAN <span className="text-muted">(facoltativo)</span></Form.Label>
                <Form.Control value={modifica.iban} onChange={campo('iban')} />
              </Form.Group>
              {!modifica.id && (
                <Row className="g-3 mb-3">
                  <Col>
                    <Form.Label>Saldo iniziale (€)</Form.Label>
                    <Form.Control type="number" step="0.01" value={modifica.saldo_iniziale} onChange={campo('saldo_iniziale')} />
                  </Col>
                  <Col>
                    <Form.Label>alla data</Form.Label>
                    <Form.Control type="date" value={modifica.data_saldo_iniziale} onChange={campo('data_saldo_iniziale')} />
                  </Col>
                </Row>
              )}
              <Form.Label>Colore</Form.Label>
              <div className="d-flex flex-wrap gap-2 mb-3">
                {COLORI.map((c) => (
                  <button key={c} type="button" onClick={() => setModifica((m) => ({ ...m, colore: c }))}
                    style={{ width: 30, height: 30, borderRadius: '50%', background: c, cursor: 'pointer',
                      border: modifica.colore === c ? '3px solid #212529' : '2px solid #fff', boxShadow: '0 0 0 1px #ced4da' }} />
                ))}
              </div>
              <Form.Label>Icona</Form.Label>
              <div className="d-flex flex-wrap gap-2 mb-3">
                {ICONE_RAPPORTI.map((k) => (
                  <button key={k} type="button" onClick={() => setModifica((m) => ({ ...m, icona: k }))}
                    style={{ width: 40, height: 40, borderRadius: 10, cursor: 'pointer', background: modifica.icona === k ? tema.morbido : '#fff',
                      border: modifica.icona === k ? `2px solid ${tema.colore}` : '1px solid #ced4da' }}>
                    <FontAwesomeIcon icon={ICONE[k]} />
                  </button>
                ))}
              </div>
              <Form.Group>
                <Form.Label>Nome del conto in Firefly <span className="text-muted">(facoltativo)</span></Form.Label>
                <Form.Control value={modifica.firefly_asset_account_name} onChange={campo('firefly_asset_account_name')}
                  placeholder={modifica.id ? '' : 'Se vuoto uso il nome del rapporto in maiuscolo'} />
                <Form.Text className="text-muted">
                  {modifica.id ? 'Cambiandolo si rinomina anche il conto in Firefly.' : 'Se esiste già in Firefly lo collego, altrimenti lo creo.'}
                </Form.Text>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setModifica(null)}>Annulla</Button>
              <Button type="submit" disabled={salvataggio} style={{ background: tema.colore, borderColor: tema.colore }}>
                {salvataggio ? 'Salvo…' : 'Salva'}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>
    </>
  );
};

export default RapportiBancari;
